// 3rd party
import { Component, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatExpansionPanel, MatTabGroup } from '@angular/material';
import { TranslateService } from '@ngx-translate/core';
import * as FileSaver from 'file-saver';
import { Subscription } from 'rxjs/Rx';
import { PdfJsViewerComponent } from 'ng2-pdfjs-viewer';

// components
import { AppComponent } from 'app/app.component';
import { LayoutMainComponent } from 'app/shared/layout/layout-main/layout-main.component';
import { AssayListComponent } from 'app/assays/assay-list/assay-list.component';

// helpers
import { Helper } from 'app/shared/helpers/helper';

// models
import { Calibration } from 'app/calibrations/shared/calibration.model';
import { StoredFile } from 'app/shared/models/stored-file.model';

// services
import { CalibrationService } from 'app/calibrations/shared/calibration.service';
import { DialogsService } from 'app/shared/services/dialog/dialog.service';
import { InstrumentService } from 'app/instruments/shared/instrument.service';
import { StoredFileService } from 'app/shared/services/stored-file.service';

@Component({
  selector: 'app-assay',
  templateUrl: './assay.component.html',
  styleUrls: ['./assay.component.css']
})
export class AssayComponent implements OnInit, OnDestroy {

  @ViewChild('assayPanel') assayPanel: MatExpansionPanel;
  @ViewChild('matTabGroup') matTabGroup: MatTabGroup;
  @ViewChild('pdfViewer') pdfViewer: PdfJsViewerComponent;

  assay: Calibration = new Calibration();
  attachments: any[] = new Array<any>();
  checkedAttachments: any;
  chkAllAttachments: boolean = false;
  isLoading: boolean;
  subscribe1: Subscription;

  constructor(
    private activatedRoute: ActivatedRoute,
    private app: AppComponent,
    public assayListComponent: AssayListComponent,
    public calibrationService: CalibrationService,
    public dialog: DialogsService,
    public instrumentService: InstrumentService,
    public mainLayout: LayoutMainComponent,
    public router: Router,
    public storedFileService: StoredFileService,
    public translate: TranslateService
  ) { }

  ngOnInit() {
    this.assayPanel.expanded = true;

    this.subscribe1 = this.activatedRoute.params.subscribe(params => {
      let id = params['identifier'];
      if (!id) {
        return;
      }
      this.assayListComponent.sidenavRight.open();

      this.calibrationService.get(id, null, null, null, null, null, '"IsAssay_=1"', '"CalibrationStatus","CustomerStatus","Code","Date","IsField","CustomerApproveDate","CustomerStatusDescription","Comments","Instrument.Code","Instrument.CollectDate","Instrument.SampleExpirationDate","Instrument.CollectPlaceName","Instrument.CollectPlace","Instrument.InstrumentType.Name","CalibrationType.Name","OrderService.Code","OrderService.ReceivingDate","ApproveContact.Name"').subscribe(res => {
        this.assay = Helper.deserialize(res.text())[0];
        this.loadAttachments();
      });
    });
  }

  ngOnDestroy() {
    if (this.subscribe1) {
      this.subscribe1.unsubscribe();
    }
    this.assayListComponent.sidenavRight.close();
  }

  checkAllAttachments(e) {
    this.attachments.forEach((item) => {
      item.checked = e.source.checked;
    });
    this.checkedAttachments = this.attachments.filter(item => item.checked).length;
  }

  close() {
    this.assayListComponent.sidenavRight.close();
    this.router.navigate([`/${this.app.currentContact.Domain}/assays/`]);
  }

  downloadCal() {
    this.isLoading = true;
    let params = [];
    params[0] = {
      identifier: this.assay.Identifier,
      tableName: 'new_calibrations'
    };

    this.calibrationService.download(params)
      .subscribe(data => {
        this.isLoading = false;
        if (data) {
          FileSaver.saveAs(data, `${this.assay.Code}.cal`);
        } else {
          this.dialog.error('Message.ErrorAlert', 'Message.DownloadAssayError');
        }
      }, () => {
        this.isLoading = false;
        this.dialog.error('Message.ErrorAlert', 'Message.DownloadAssayError');
      });
  }

  downloadAttachment() {
    this.isLoading = true;
    let lst = this.attachments.filter(at => at.checked);
    let files: any[] = new Array<any>();
    let fileName: string = '';

    lst.forEach(element => {
      (fileName = this.storedFileService.formatUserFileName(element.UserFileName));
      files.push({ identifier: element.Identifier, file: fileName ? fileName : element.SystemFileName, size: this.storedFileService.formatFileSize(element.size), systemFileName: element.SystemFileName, userFileName: element.UserFileName });
    });

    this.storedFileService.download(files)
      .subscribe(data => {
        if (data) {
          let fileName = files.length === 1 ? files[0].file : this.assay.Code;
          FileSaver.saveAs(data, fileName);
          this.isLoading = false;
        } else {
          this.isLoading = false;
          this.dialog.error('Message.ErrorAlert', 'Message.ViewAttachmentError');
        }
      }, () => {
        this.isLoading = false;
        this.dialog.error('Message.ErrorAlert', 'Message.ViewAttachmentError');
      });
  }

  getAttachmentName(sf: StoredFile): string {
    let name = this.storedFileService.formatUserFileName(sf.UserFileName);
    return name ? name : sf.SystemFileName;
  }

  getSituationDescription(status: number) {
    return this.calibrationService.getSituationDescription(status);
  }

  getSituationIcon(status: number) {
    return this.calibrationService.getSituationIcon(status);
  }

  getStatusIcon(status: number) {
    return this.calibrationService.getStatusIcon(status);
  }

  loadAttachments() {
    this.isLoading = true;
    let lstFilters = '"Calibration.Identifier_=\'' + this.assay.Identifier + '\'","IsAttach"';
    let lstFields = '"SystemFileName","UserFileName","Size"';

    this.storedFileService
      .get(null, null, null, null, null, null, lstFilters, lstFields)
      .subscribe(values => {
        this.isLoading = false;
        this.attachments = values.json();

        if (this.attachments.length > 0) {
          this.attachments.forEach(element => {
            element.checked = false;
          });
        } else {
          this.attachments = null;
        }

      }, err => {
        this.isLoading = false;
        this.attachments = null;
      });
  }

  onAttachmentSelecionChange() {
    if (this.attachments.filter(item => item.checked).length !== this.attachments.length && this.chkAllAttachments) {
      this.chkAllAttachments = false;
    }
    if (this.attachments.filter(item => item.checked).length === this.attachments.length && !this.chkAllAttachments) {
      this.chkAllAttachments = true;
    }

    this.checkedAttachments = this.attachments.filter(item => item.checked).length;
  }

  openAttachment() {
    this.isLoading = true;
    let lst = this.attachments.filter(at => at.checked);
    let file: any[] = new Array<any>();

    let fileName = this.storedFileService.formatUserFileName(lst[0].UserFileName);
    file.push({ identifier: lst[0].Identifier, file: fileName ? fileName : lst[0].SystemFileName, size: this.storedFileService.formatFileSize(lst[0].size), SystemFileName: lst[0].SystemFileName, userFileName: lst[0].UserFileName });

    this.storedFileService.download(file)
      .subscribe(data => {
        if (data) {
          const blob = new Blob([data], { type: data.type });

          if (blob.type === 'application/pdf') {
            this.pdfViewer.pdfSrc = blob;
            this.pdfViewer.refresh();
          } else {
            const fileUrl = URL.createObjectURL(data);
            const newWindow = window.open(fileUrl);

            if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
              this.dialog.openPopupDialog();
            }
          }

          this.isLoading = false;
        } else {
          this.dialog.error('Message.ErrorAlert', 'Message.ViewAttachmentError');
          this.isLoading = false;
        }
      }, () => {
        this.isLoading = false;
        this.dialog.error('Message.ErrorAlert', 'Message.ViewAttachmentError');
      });
  }

  openCustomerStatusDialog(readOnly: boolean) {
    let lst: any[] = [];
    lst[0] = this.assay;
    this.dialog.customerStatus(lst, readOnly).subscribe((res) => {
      if (res === undefined && !readOnly) {
        this.ngOnInit();
        this.assayListComponent.refresh();
      }
    });
  }

  setCustomerStatus() {
    this.openCustomerStatusDialog(false);
  }

  view(download: boolean) {
    this.isLoading = true;
    let lstFilters = '"Calibration.Identifier_in(\'' + this.assay.Identifier + '\')"';
    let lstFields = '"SystemFileName","Calibration.Id","Calibration.Code","Calibration.Date"';
    let storedFiles: any[] = [];

    this.storedFileService.get(null, null, null, null, null, null, lstFilters, lstFields)
      .subscribe(value => {
        storedFiles = value.json() as StoredFile[];
        if (!storedFiles || storedFiles.length === 0) {
          this.dialog.error('Message.ErrorAlert', 'Message.ViewAssayError', 'info_outline', '#009688');
          this.isLoading = false;
        }
        this.storedFileService.download(storedFiles)
          .subscribe(data => {
            this.isLoading = false;
            if (data) {
              const file = new Blob([data], { type: data.type });

              this.storedFileService.view(file, download, this.pdfViewer, `Ensaio ${storedFiles[0].Calibration.Code}`);

            } else {
              this.dialog.error('Message.ErrorAlert', 'Message.ViewAssayError');
              this.isLoading = false;
            }
          }, () => {
            this.dialog.error('Message.ErrorAlert', 'Message.OpenAssayError');
            this.isLoading = false;
          });
      }, () => {
        this.dialog.error('Message.ErrorAlert', 'Message.OpenAssayError');
        this.isLoading = false;
      });
  }

  viewCustomerStatus() {
    this.openCustomerStatusDialog(true);
  }
}



// WEBPACK FOOTER //
// ./src/app/assays/assay/assay.component.ts